<template>
  <v-container>
    <template v-if="setupStateLoading">
      <Loader class="mr-2 mt-6" />
    </template>
    <template v-else>
      <v-stepper v-if="step !== null" v-model="step">
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" step="1">
            {{ $vuetify.lang.t("$vuetify.embeddingModel.embedding_model_title") }}
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 2" step="2">
            {{ `${$vuetify.lang.t("$vuetify.maira_setup.upload_dataset")}` }}
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 3" step="3" v-if="!['dictionary', 'ts'].includes(datasetType)">
            {{ `${$vuetify.lang.t("$vuetify.maira_setup.dataset_training")}` }}
          </v-stepper-step>
          <v-divider v-if="!['dictionary', 'ts'].includes(datasetType)"></v-divider>
          <v-stepper-step :step="['dictionary', 'ts'].includes(datasetType) ? 3 : 4">
            {{ `${$vuetify.lang.t("$vuetify.maira_setup.profile_menu_txt")}` }}
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <EmbeddedModel
              ref="embeddedModel"
              mode="create"
              :model="embeddingModel"
              :disabled="gptSetupState.training && gptSetupState.training.completed"
              @model-loaded="onModelLoaded"
            />
            <v-row>
              <v-col cols="12" md="8"> </v-col>
              <v-col
                cols="12"
                md="4"
                class="d-flex flex-column flex-md-row align-start align-md-center justify-start justify-md-end"
              >
                <v-btn
                  v-if="gptSetupState.embedding && gptSetupState.embedding.completed && !loading"
                  class="mb-2 mb-md-0 mr-md-4 primary--text"
                  outlined
                  @click="skip(step)"
                >
                  {{ $vuetify.lang.t("$vuetify.common.skip_update") }}
                </v-btn>
                <v-btn :loading="loading" color="primary" class="white--text" @click="nextStep(step)">
                  {{ $vuetify.lang.t("$vuetify.common.continue_btn") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="2">
            <UploadDataset
              @mandatory-status="isCheckingMandatoryColumns = $event"
              ref="dataset"
              :is-url-reachable.sync="isUrlReachable"
              :is-reachabilty-checking="isReachabiltyChecking"
              @open-cloud-picker="openCloudPickerForSelection"
            />
            <v-col class="d-flex justify-space-between">
              <v-btn
                :style="{ visibility: !datasetCreateLoader && !isCheckingMandatoryColumns ? 'visible' : 'hidden' }"
                class="mr-4 primary--text"
                outlined
                :loading="false"
                @click="previousStep(step)"
              >
                {{ $vuetify.lang.t("$vuetify.common.prev_btn") }}
              </v-btn>
              <div>
                <v-btn
                  outlined
                  v-if="!gptSetupState.dataset.completed && !datasetCreateLoader"
                  class="mr-4 primary--text"
                  @click="nextStep(step, 'secondStep')"
                >
                  {{ $vuetify.lang.t("$vuetify.common.skip") }}
                </v-btn>
                <v-btn
                  outlined
                  v-if="gptSetupState.dataset.completed && !datasetCreateLoader"
                  class="mr-4 primary--text"
                  @click="skip(step)"
                >
                  {{ $vuetify.lang.t("$vuetify.common.skip_update") }}
                </v-btn>
                <v-btn
                  class="mr-4"
                  :loading="datasetCreateLoader"
                  @click="nextStep(step)"
                  color="primary"
                  :disabled="isCheckingMandatoryColumns"
                >
                  {{ $vuetify.lang.t("$vuetify.common.continue_btn") }}
                </v-btn>
              </div>
            </v-col>
          </v-stepper-content>

          <v-stepper-content v-if="datasetType === 'ts'" step="3">
            <div v-if="datasetFileUploadInProgress">
              <div style="min-height: 200px" class="d-flex justify-center align-center">
                <v-col cols="6">
                  <v-list-item>
                    <v-list-item-icon>
                      <v-progress-circular indeterminate color="primary"> </v-progress-circular>
                    </v-list-item-icon>

                    <v-list-item-content>
                      <v-list-item-title>
                        {{ $vuetify.lang.t("$vuetify.gpt_dataset.dataset_ts_process_progress") }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </div>
            </div>
            <v-card
              v-else-if="gptSetupState?.dataset?.error"
              class="pa-2 d-flex flex-column align-center justify-center"
              height="200px"
            >
              <v-col class="d-flex align-center justify-center" cols="auto">
                <v-icon color="red">mdi-close-circle-outline</v-icon>
                <v-list-item-title class="ml-2">
                  {{ gptSetupState.dataset.message }}
                </v-list-item-title>
              </v-col>
              <v-btn class="mt-1 primary--text" outlined :loading="false" @click="datasetErrorTryAgain">
                {{ $vuetify.lang.t("$vuetify.common.upload_again") }}
              </v-btn>
            </v-card>
          </v-stepper-content>
          <v-stepper-content step="3" v-if="!this.isSkipTraining">
            <div v-if="datasetFileUploadInProgress">
              <div style="min-height: 200px" class="d-flex justify-center align-center">
                <v-col cols="6">
                  <v-list-item>
                    <v-list-item-icon>
                      <v-progress-circular indeterminate color="primary"> </v-progress-circular>
                    </v-list-item-icon>

                    <v-list-item-content>
                      {{ $vuetify.lang.t("$vuetify.gpt_dataset.dataset_process_progress") }}
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </div>
            </div>
            <v-card
              v-else-if="gptSetupState?.dataset?.error"
              class="pa-2 d-flex flex-column align-center justify-center"
              height="200px"
            >
              <v-col class="d-flex align-center justify-center" cols="auto">
                <v-icon color="red">mdi-close-circle-outline</v-icon>
                <v-list-item-title class="ml-2">
                  {{ gptSetupState?.dataset?.message }}
                </v-list-item-title>
              </v-col>
              <v-btn class="mt-1 primary--text" outlined :loading="false" @click="datasetErrorTryAgain">
                {{ $vuetify.lang.t("$vuetify.common.upload_again") }}
              </v-btn>
            </v-card>
            <!-- training error -->
            <v-card
              v-else-if="gptSetupState?.training?.error"
              class="pa-2 d-flex flex-column align-center justify-center"
              height="200px"
            >
              <v-col class="d-flex align-center justify-center" cols="auto">
                <v-icon color="red">mdi-close-circle-outline</v-icon>
                <v-list-item-title class="ml-2">
                  {{ gptSetupState?.training?.message }}
                </v-list-item-title>
              </v-col>
              <v-btn class="mr-4 mt-1 primary--text" outlined :loading="false" @click="trainingErrorTryAgain">
                {{ $vuetify.lang.t("$vuetify.common.try_again") }}
              </v-btn>
            </v-card>

            <div v-else-if="dataSetProcessCompleted">
              <v-card v-if="trainingInProgress" height="200px" class="d-flex justify-center align-center">
                <v-col cols="6">
                  <v-list-item>
                    <v-list-item-icon>
                      <v-progress-circular indeterminate color="primary"> </v-progress-circular>
                    </v-list-item-icon>

                    <v-list-item-content>
                      <v-list-item-title>
                        {{ $vuetify.lang.t("$vuetify.gpt_dataset.training_in_progress") }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </v-card>
              <v-form ref="train_form" v-else>
                <v-card height="200px" style="box-shadow: none" class="d-flex justify-center align-center">
                  <v-col cols="12" md="6">
                    <v-select
                      :label="$vuetify.lang.t('$vuetify.gpt_dataset.training_type')"
                      :items="[
                        {
                          text: this.$vuetify.lang.t('$vuetify.gpt_dataset.text_training_type'),
                          value: 'text',
                        },
                        {
                          text: this.$vuetify.lang.t('$vuetify.gpt_dataset.image_training_type'),
                          value: 'image',
                        },
                      ]"
                      v-model="trainType"
                      outlined
                      :rules="[required($vuetify.lang.t('$vuetify.gpt_dataset.training_type'))]"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="batchSize"
                      :label="$vuetify.lang.t('$vuetify.gpt_dataset.batch_size')"
                      outlined
                      type="number"
                      :rules="batchSizeRules"
                      disabled
                    ></v-text-field>
                  </v-col>
                </v-card>
              </v-form>

              <div v-if="!trainingInProgress" class="d-flex justify-space-between">
                <v-btn
                  :style="{ visibility: !loading ? 'visible' : 'hidden' }"
                  class="mr-4 primary--text"
                  outlined
                  :loading="false"
                  @click="previousStep(step)"
                >
                  {{ $vuetify.lang.t("$vuetify.common.prev_btn") }}
                </v-btn>
                <div class="d-flex justify-space-between">
                  <v-btn
                    v-if="gptSetupState.training && gptSetupState.training?.completed && !loading"
                    outlined
                    class="mr-4 primary--text"
                    @click="skip(step)"
                  >
                    {{ $vuetify.lang.t("$vuetify.common.skip_update") }}
                  </v-btn>
                  <div>
                    <v-btn
                      v-if="!gptSetupState.training?.completed && !loading"
                      outlined
                      class="primary--text mr-2"
                      @click="nextStep(step, 'thirdStep')"
                    >
                      {{ $vuetify.lang.t("$vuetify.common.skip") }}
                    </v-btn>
                  </div>
                  <v-btn @click="handleTraining" class="mr-4 pr-8 pl-8" :loading="loading" color="primary">
                    {{ $vuetify.lang.t("$vuetify.maira_setup.dataset_training") }}
                  </v-btn>
                </div>
              </div>
            </div>
            <template v-else>
              <v-card style="box-shadow: none" height="200px" class="d-flex justify-center align-center">
                <v-col cols="6">
                  <v-list-item>
                    <v-list-item-icon>
                      <v-progress-circular indeterminate color="primary"> </v-progress-circular>
                    </v-list-item-icon>

                    <v-list-item-content>
                      {{ $vuetify.lang.t("$vuetify.gpt_dataset.dataset_process_progress") }}
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </v-card>
              <v-btn class="mr-4 primary--text" outlined :loading="false" @click="previousStep(step)">
                {{ $vuetify.lang.t("$vuetify.common.prev_btn") }}
              </v-btn>
            </template>
          </v-stepper-content>
          <v-stepper-content step="4">
            <div v-if="isDictionaryProcessing">
              <div style="min-height: 200px" class="d-flex justify-center align-center">
                <v-col cols="6">
                  <v-list-item>
                    <v-list-item-icon>
                      <v-progress-circular indeterminate color="primary"> </v-progress-circular>
                    </v-list-item-icon>

                    <v-list-item-content>
                      {{ $vuetify.lang.t("$vuetify.gpt_dataset.dataset_process_profile_progress") }}
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </div>
            </div>
            <v-card
              v-else-if="gptSetupState?.dataset?.error"
              class="pa-2 d-flex flex-column align-center justify-center"
              height="200px"
            >
              <v-col class="d-flex align-center justify-center" cols="auto">
                <v-icon color="red">mdi-close-circle-outline</v-icon>
                <v-list-item-title class="ml-2">
                  {{ gptSetupState?.dataset?.message }}
                </v-list-item-title>
              </v-col>
              <v-btn class="mt-1 primary--text" outlined :loading="false" @click="datasetErrorTryAgain">
                {{ $vuetify.lang.t("$vuetify.common.upload_again") }}
              </v-btn>
            </v-card>
            <template v-else>
              <CreateGptProfile
                ref="profile"
                :key="'profile-' + profileRefreshKey"
                :showConfig="false"
                :showLiffConfig="false"
              />
              <v-col class="d-flex justify-space-between">
                <v-btn
                  :style="{ visibility: !loading ? 'visible' : 'hidden' }"
                  class="mr-4 primary--text"
                  outlined
                  :loading="false"
                  @click="previousStep(step)"
                >
                  {{ $vuetify.lang.t("$vuetify.common.previous") }}
                </v-btn>
                <div>
                  <v-btn v-if="!loading" outlined class="primary--text mr-2" @click="nextStep(step, 'forthstep')">
                    {{ $vuetify.lang.t("$vuetify.common.skip") }}
                  </v-btn>
                  <v-btn :loading="loading" color="primary" class="white--text" @click="nextStep(step)">
                    {{ $vuetify.lang.t("$vuetify.common.continue_btn") }}
                  </v-btn>
                </div>
              </v-col>
            </template>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </template>

    <CloudFilePicker
      v-if="cloudPickerOpen"
      v-model="cloudPickerOpen"
      :project-id="$route.query.pid"
      :provider="selectedGptDataset.cloudProvider"
      :allowed-extensions="cloudFileExtensions"
      :existing-items="existingCloudItems"
      @select="handleCloudFileSelect"
      @cancel="handleCloudPickerCancel"
    />
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import EmbeddedModel from "@/components/Projects/EmbeddedModel/EmbeddedModel";
import UploadDataset from "@/components/Projects/UploadDataset/UploadDataset";
import CloudFilePicker from "@/components/Projects/UploadDataset/CloudFilePicker";
import CreateGptProfile from "@/components/Projects/CreateGptProfile/CreateGptProfile";
import Loader from "@/components/common/loader/Loader";
import {
  GET_GPT_PROFILE_BY_ID,
  CREATE_GPT_PROFILE,
  UPDATE_GPT_PROFILE_STATE,
  UPDATE_GPT_PROFILE_API,
  ADD_ALERT,
  GET_GPT_DATASET_BY_ID,
  CREATE_GPT_DATASET,
  CREATE_GPT_DATASET_TS,
  UPDATE_GPT_DATASET_API,
  TRAIN_GPT_DATASET,
  UPDATE_GPT_DATASET_FILE,
  UPDATE_GPT_DATASET_TS_FILE,
  CREATE_GPT_SETTINGS,
  UPDATE_GPT_SETUP_STATE,
  GET_TASK_STATUS,
  UPDATE_GPT_DATASET_STATE,
  DB_STORE_TASKS_STATUS,
  CREATE_SETTINGS,
  GET_SETTINGS,
  GET_CLOUD_STORAGE_ITEMS,
} from "@/store/_actiontypes";
import { GptProfileMapper } from "@/helpers/dataMappers/gptProfile";
import { GptDatasetMapper } from "@/helpers/dataMappers/gptDataset";
import getEnv from "@/config/env";
import { datasetMixin, cloudSelectionIdsEqual } from "@/mixins/datasetMixin";
import { Toast } from "@/helpers/toast";

export default {
  mixins: [datasetMixin],
  components: {
    UploadDataset,
    CloudFilePicker,
    CreateGptProfile,
    Loader,
    EmbeddedModel,
  },
  data() {
    return {
      loading: false,
      isTimeSeries: false,
      step: null,
      datasetFileUploadInProgress: false,
      trainingInProgress: false,
      trainType: "",
      batchSize: 1,
      dataSetProcessCompleted: false,
      intervalId: null,
      datasetCreateLoader: false,
      setupStateLoading: false,
      batchSizeRules: [value => value >= 1 || "Min value is 1", value => value <= 10 || "Max value is 10"],
      embeddingModel: ["text-embedding-ada-002", "text-embedding-3-small"],
      selectedModel: "",
      profileRefreshKey: 0,
      isCheckingMandatoryColumns: false,
      isReachabiltyChecking: false,
      isUrlReachable: true,
      prevScrapeUrls: null,
    };
  },
  computed: {
    ...mapState({
      gptProfiles: state => state.project.gptProfiles?.profiles || [],
      selectedGptProfile: state => state.project.selectedGptProfile,
      gptDatasets: state => state.project.gptDatasets.datasets,
      selectedGptDataset: state => state.project.selectedGptDataset,
      profileExist: state => (state.project.gptProfiles?.profiles || [])?.length > 0,
      datasetExist: state => state.project.gptSetupState.dataset.completed,
      gptSetupState: state => state.project.gptSetupState,
      selectedProject: state => state.project.selectedProject,
    }),
    isUpdateMode() {
      return !!(this.gptSetupState?.dataset?.dataset_id || !!this.selectedGptDataset?.datasetId);
    },
    isSkipTraining() {
      return ["dictionary", "ts"].includes(this.datasetType);
    },
    isDictionaryDataset() {
      return (
        Array.isArray(this.$store.state.project.selectedGptDataset?.tags) &&
        this.$store.state.project.selectedGptDataset.tags?.includes("dictionary")
      );
    },
    isDictionaryProcessing() {
      return this.isDictionaryDataset && this.datasetFileUploadInProgress;
    },
  },
  watch: {
    isDictionaryDataset(newVal) {
      if (newVal && this.step === 3) this.step = 4;
    },
    gptSetupState: {
      handler(setupState) {
        if (!setupState) return;

        if (setupState.dataset?.task_id && !this.intervalId && !setupState.dataset?.error) {
          this.datasetFileUploadInProgress = true;
          if (this.datasetType === "ts") {
            this.step = 3;
          }
          this.startInterval(setupState.dataset?.task_id, "dataset");
        }
        if (setupState.training?.task_id && !this.intervalId && !setupState.training?.error) {
          this.startInterval(setupState.training?.task_id, "train");
          this.trainingInProgress = true;
        }

        if (setupState.dataset?.completed) {
          this.dataSetProcessCompleted = true;
        }
        if (setupState.training?.completed) {
          this.trainingInProgress = false;
          if (this.step === 3) {
            this.step = 4;
          }
        }
      },
      immediate: true,
      deep: true,
    },
    step: {
      handler(newVal, oldVal) {
        if (newVal === 4 && oldVal !== 4) {
          this.profileRefreshKey += 1;
        }
      },
      immediate: true,
    },
    "selectedGptDataset.url"(newUrl, oldUrl) {
      if (newUrl !== oldUrl) {
        this.datasetFileUploadInProgress = false;
        this.isUrlReachable = true;
        this.stopInterval();
        this.UPDATE_GPT_SETUP_STATE({
          ...this.gptSetupState,
          dataset: {
            ...this.gptSetupState.dataset,
            task_id: null,
            status: null,
            message: null,
            error: false,
            completed: false,
            skipped: false,
          },
        });
      }
    },
  },
  mounted() {
    this.loadGptSetupInitialData().then(() => {
      if (
        this.gptSetupState.embedding?.completed &&
        this.gptSetupState?.dataset?.completed &&
        this.gptSetupState?.training?.completed
      ) {
        this.step = 4;
      } else if (this.gptSetupState.embedding?.completed && this.gptSetupState?.dataset?.completed) {
        if (this.isSkipTraining) {
          this.step = 4;
        } else {
          this.step = 3;
        }
      } else if (this.gptSetupState.embedding?.completed) {
        this.step = 2;
      } else {
        this.step = 1;
      }
      if (this.selectedModel) {
        this.$nextTick(() => {
          if (this.$refs.embeddedModel) {
            this.$refs.embeddedModel.selectedModel = this.selectedModel;
          }
        });
      }
    });
  },

  beforeDestroy() {
    this.stopInterval();
    this.resetGptProfile();
    this.resetGptDataset();
  },

  methods: {
    ...mapActions("project", [
      GET_GPT_PROFILE_BY_ID,
      CREATE_GPT_PROFILE,
      UPDATE_GPT_PROFILE_STATE,
      UPDATE_GPT_PROFILE_API,
      GET_GPT_DATASET_BY_ID,
      CREATE_GPT_DATASET,
      CREATE_GPT_DATASET_TS,
      UPDATE_GPT_DATASET_API,
      UPDATE_GPT_DATASET_FILE,
      UPDATE_GPT_DATASET_TS_FILE,
      TRAIN_GPT_DATASET,
      CREATE_GPT_SETTINGS,
      UPDATE_GPT_SETUP_STATE,
      UPDATE_GPT_DATASET_STATE,
      DB_STORE_TASKS_STATUS,
      CREATE_SETTINGS,
      GET_SETTINGS,
      GET_CLOUD_STORAGE_ITEMS,
    ]),
    ...mapActions("task", [GET_TASK_STATUS]),
    stopInterval() {
      clearInterval(this.intervalId);
      this.intervalId = null;
    },
    resetGptProfile() {
      this.UPDATE_GPT_PROFILE_STATE({
        profile: GptProfileMapper.mapBackendToFrontend(),
      });
    },
    loadGptSetupInitialData() {
      this.setupStateLoading = true;

      // Return the Promise so we can chain `.then()` in mounted
      return this.GET_SETTINGS({
        project_id: this.$route.query.pid,
        settingsType: "general",
        collectionType: "item",
      })
        .then(response => {
          const fetchedModel = response?.general?.embedding_model;

          if (fetchedModel) {
            this.UPDATE_GPT_SETUP_STATE({
              ...this.gptSetupState,
              embedding: {
                completed: true,
                model: fetchedModel,
              },
            });

            if (this.gptDatasets.length > 0) {
              return this.GET_GPT_DATASET_BY_ID({
                project_id: this.$route.query.pid,
                dataset_id: this.gptDatasets[0]?.dataset_id,
              })
                .then(_profileRes => {
                  this.hydrateRestoredCloudItems();
                  const resDataset = _profileRes?.data?.response?.dataset;
                  this.setDatasetType(resDataset);

                  if (this.datasetType === "url") {
                    if (resDataset?.social_media_type) {
                      this.contentSource = "socialMedia";
                    }
                    const resUrls = resDataset?.urls || [];
                    this.prevScrapeUrls = [...resUrls];
                  }

                  if (this.gptSetupState.training?.completed) {
                    this.step = 4;
                  }
                })
                .catch(_err => {
                  this.$store.dispatch(
                    `alert/${ADD_ALERT}`,
                    {
                      message: this.$vuetify.lang.t("$vuetify.gpt_profile.profile_fetch_error"),
                      color: "error",
                    },
                    { root: true }
                  );
                });
            }
          }
        })
        .catch(_err => {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_fetch_error"),
              color: "error",
            },
            { root: true }
          );
        })
        .finally(() => {
          this.setupStateLoading = false;
        });
    },
    // Datasets persist only cloud item ids, so GptDatasetMapper marks restored items with _restored and this resolves them back to real names/sizes.
    async hydrateRestoredCloudItems() {
      const sources = this.selectedGptDataset?.cloudSources || [];
      const restored = sources.filter(
        (s) => (s.items || []).some((i) => i._restored),
      );
      if (!restored.length) return;
      const hydrated = await Promise.all(
        restored.map(async (s) => {
          try {
            const res = await this.GET_CLOUD_STORAGE_ITEMS({
              project_id: this.$route.query.pid,
              provider: s.provider,
              account_id: s.account_id,
              item_ids: s.items.map((i) => i.id),
            });
            const byId = new Map(
              (res.data?.detail?.items || []).map((i) => [i.id, i]),
            );
            return {
              ...s,
              items: s.items.map((item) => {
                const meta = byId.get(item.id);
                if (!meta || meta.missing) return item;
                return {
                  id: item.id,
                  name: meta.name || item.id,
                  size: meta.size,
                  is_folder: !!meta.is_folder,
                  mime_type: meta.mime_type,
                  account_id: s.account_id,
                };
              }),
            };
          } catch (e) {
            return s;
          }
        }),
      );
      const merged = sources.map(
        (s) =>
          hydrated.find(
            (h) => h.provider === s.provider && h.account_id === s.account_id,
          ) || s,
      );
      const dataset = this.selectedGptDataset;
      if (
        dataset?.uploadSource === "cloud" &&
        cloudSelectionIdsEqual(dataset.cloudSources, merged)
      ) {
        this.UPDATE_GPT_DATASET_STATE({
          dataset: { ...dataset, cloudSources: merged },
        });
      }
    },
    resetGptDataset() {
      this.UPDATE_GPT_DATASET_STATE({
        dataset: GptDatasetMapper.mapBackendToFrontend({
          dataset_id: "",
          description: "",
          idx_column_name: "",
          image_url_column: "",
          is_idx_fillup_if_empty: true,
          name: "",
          secondary_idx_column: "",
        }),
      });
    },
    startInterval(task_id, type) {
      this.intervalId = setInterval(() => {
        if (type === "dataset") {
          this.GET_TASK_STATUS({
            project_id: this.selectedProject.id,
            task_id: task_id,
          })
            .then(res => {
              if (res.data.detail?.response?.status === "success") {
                this.$store.dispatch(
                  `alert/${ADD_ALERT}`,
                  {
                    message: "Dataset Processing Successful",
                    color: "success",
                  },
                  { root: true }
                );
                this.stopInterval();
                this.dataSetProcessCompleted = true;
                this.datasetFileUploadInProgress = false;
                if (this.datasetType === "ts") {
                  const updatedState = {
                    ...this.gptSetupState,
                    dataset: {
                      ...this.gptSetupState.dataset,
                      completed: true,
                      dataset_id: this.gptSetupState.dataset.dataset_id,
                      task_id: null,
                      error: false,
                      skipped: false,
                    },
                    training: {
                      ...this.gptSetupState.training,
                      completed: true,
                      task_id: null,
                      status: "success",
                      error: false,
                      skipped: false,
                    },
                  };
                  this.UPDATE_GPT_SETUP_STATE(updatedState);
                } else {
                  this.UPDATE_GPT_SETUP_STATE({
                    ...this.gptSetupState,
                    dataset: {
                      ...this.gptSetupState.dataset,
                      completed: true,
                      dataset_id: this.gptSetupState.dataset.dataset_id,
                      task_id: null,
                      error: false,
                      skipped: false,
                    },
                  });
                }

                this.DB_STORE_TASKS_STATUS({
                  project_id: this.selectedProject.id,
                  tasks_status: {
                    ...this.gptSetupState,
                    dataset: {
                      ...this.gptSetupState.dataset,
                      completed: true,
                      dataset_id: this.gptSetupState.dataset.dataset_id,
                      task_id: null,
                      error: false,
                      skipped: false,
                    },
                  },
                })
                  .then(_response => {})
                  .catch(error => {
                    console.error(error.response);
                  });
              } else if (
                res.data?.detail?.response?.status !== "pending" &&
                res.data?.detail?.response?.status !== "claimable"
              ) {
                this.stopInterval();
                this.datasetFileUploadInProgress = false;
                this.UPDATE_GPT_SETUP_STATE({
                  ...this.gptSetupState,
                  dataset: {
                    ...this.gptSetupState.dataset,
                    status: res.data.detail?.response.status,
                    message: res.data.detail?.response?.message,
                    error: true,
                  },
                });
                this.DB_STORE_TASKS_STATUS({
                  project_id: this.selectedProject.id,
                  tasks_status: {
                    ...this.gptSetupState,
                    dataset: {
                      ...this.gptSetupState.dataset,
                      status: res.data.detail?.response.status,
                      message: res.data.detail?.response?.message,
                      error: true,
                    },
                  },
                })
                  .then(_response => {})
                  .catch(error => {
                    console.error(error.response);
                  });
              } else {
                this.UPDATE_GPT_SETUP_STATE({
                  ...this.gptSetupState,
                  dataset: {
                    ...this.gptSetupState.dataset,
                    status: res.data.detail.response.status,
                    error: false,
                    skipped: false,
                  },
                });
                this.DB_STORE_TASKS_STATUS({
                  project_id: this.selectedProject.id,
                  tasks_status: {
                    ...this.gptSetupState,
                    dataset: {
                      ...this.gptSetupState.dataset,
                      status: res.data.detail.response.status,
                      error: false,
                      skipped: false,
                    },
                  },
                })
                  .then(_response => {})
                  .catch(error => {
                    console.error(error.response);
                  });
              }
            })
            .catch(err => {
              console.error("error", err);
              this.UPDATE_GPT_SETUP_STATE({
                ...this.gptSetupState,
                dataset: {
                  ...this.gptSetupState.dataset,
                  status: "failed",
                  message: err?.response?.data?.detail?.error,
                  error: true,
                },
              });
              this.datasetFileUploadInProgress = false;
              this.stopInterval();
            });
        }
        if (type === "train") {
          this.GET_TASK_STATUS({
            project_id: this.selectedProject.id,
            task_id: task_id,
          })
            .then(res => {
              if (res.data.detail.response.status === "success") {
                this.stopInterval();
                this.trainingInProgress = false;
                this.UPDATE_GPT_SETUP_STATE({
                  ...this.gptSetupState,
                  training: {
                    ...this.gptSetupState.training,
                    completed: true,
                    task_id: null,
                    status: "success",
                    error: false,
                    skipped: false,
                  },
                });
                this.DB_STORE_TASKS_STATUS({
                  project_id: this.selectedProject.id,
                  tasks_status: {
                    ...this.gptSetupState,
                    training: {
                      ...this.gptSetupState.training,
                      completed: true,
                      task_id: null,
                      status: "success",
                      error: false,
                      skipped: false,
                    },
                  },
                })
                  .then(_response => {})
                  .catch(error => {
                    console.error(error.response);
                  });
              } else if (
                res.data.detail.response.status !== "pending" &&
                res.data?.detail?.response?.status !== "claimable"
              ) {
                this.stopInterval();
                this.trainingInProgress = false;
                this.UPDATE_GPT_SETUP_STATE({
                  ...this.gptSetupState,
                  training: {
                    ...this.gptSetupState.training,
                    status: res.data.detail.response.status,
                    message: res.data?.detail?.response?.message,
                    error: true,
                  },
                });
                this.DB_STORE_TASKS_STATUS({
                  project_id: this.selectedProject.id,
                  tasks_status: {
                    ...this.gptSetupState,
                    training: {
                      ...this.gptSetupState.training,
                      status: res.data.detail.response.status,
                      message: res.data.detail?.response?.message,
                      error: true,
                    },
                  },
                })
                  .then(_response => {})
                  .catch(error => {
                    console.error(error.response);
                  });
                // updated training state
              } else {
                this.UPDATE_GPT_SETUP_STATE({
                  ...this.gptSetupState,
                  training: {
                    ...this.gptSetupState.training,
                    status: res.data.detail.response.status,
                    error: false,
                    skipped: false,
                  },
                });
                this.DB_STORE_TASKS_STATUS({
                  project_id: this.selectedProject.id,
                  tasks_status: {
                    ...this.gptSetupState,
                    training: {
                      ...this.gptSetupState.training,
                      status: res.data.detail.response.status,
                      error: false,
                      skipped: false,
                    },
                  },
                })
                  .then(_response => {})
                  .catch(error => {
                    console.error(error.response);
                  });
              }
            })
            .catch(err => {
              console.error("error", err);
            });
        }
      }, getEnv("VUE_APP_POLLING_INTERVAL"));
    },
    trainingErrorTryAgain() {
      this.UPDATE_GPT_SETUP_STATE({
        ...this.gptSetupState,
        training: {
          ...this.gptSetupState.training,
          task_id: null,
          status: null,
          message: null,
          error: false,
        },
      });
      this.DB_STORE_TASKS_STATUS({
        project_id: this.selectedProject.id,
        tasks_status: {
          ...this.gptSetupState,
          training: {
            ...this.gptSetupState.training,
            task_id: null,
            status: null,
            message: null,
            error: false,
          },
        },
      })
        .then(_response => {})
        .catch(error => {
          console.error(error.response);
        });
    },
    datasetErrorTryAgain() {
      this.step = 2;
      this.UPDATE_GPT_SETUP_STATE({
        ...this.gptSetupState,
        dataset: {
          ...this.gptSetupState.dataset,
          task_id: null,
          status: null,
          message: null,
          error: false,
        },
      });
      this.DB_STORE_TASKS_STATUS({
        project_id: this.selectedProject.id,
        tasks_status: {
          ...this.gptSetupState,
          dataset: {
            ...this.gptSetupState.dataset,
            task_id: null,
            status: null,
            message: null,
            error: false,
          },
        },
      })
        .then(_response => {})
        .catch(error => {
          console.error(error.response);
        });
    },
    previousStep() {
      if (this.step <= 1) {
        this.step = 1;
      } else if (this.step === 4 && (this.gptSetupState.dataset.skipped || this.isSkipTraining)) {
        this.step -= 2;
      } else {
        this.step -= 1;
      }
    },
    required(fieldName) {
      return value => {
        return !!value || `${fieldName}${this.$vuetify.lang.t("$vuetify.validation.required_error_msg_txt")}`;
      };
    },
    skip(step) {
      if (step === 4) return;
      if (this.isSkipTraining && step === 2) {
        this.step = step + 2;
        return;
      }
      this.step = step + 1;
    },
    nextStep(step, stepName) {
      if (step === 1) {
        // Validate embedding model selection
        const embeddingModelRef = this.$refs.embeddedModel;
        const isValid = embeddingModelRef.$refs.embeddingModelForm.validate();

        if (!isValid) return;

        this.loading = true;
        const settingsPayload = {
          general: {
            embedding_model: embeddingModelRef.selectedModel,
          },
        };

        this.CREATE_SETTINGS({
          project_id: this.$route.query.pid,
          embedded_models: settingsPayload,
        })
          .then(
            () => {
              this.UPDATE_GPT_SETUP_STATE({
                ...this.gptSetupState,
                embedding: {
                  ...this.gptSetupState.embedding,
                  completed: true,
                  model: embeddingModelRef.selectedModel,
                },
              });

              this.$store.dispatch(
                `alert/${ADD_ALERT}`,
                {
                  message: this.$vuetify.lang.t("$vuetify.embeddingModel.embedding_model_creation_success"),
                  color: "success",
                },
                { root: true }
              );

              // Move to next step (Dataset Upload)
              this.step = step + 1;
            },
            err => {
              this.$store.dispatch(
                `alert/${ADD_ALERT}`,
                {
                  message:
                    err.response?.data?.detail ||
                    this.$vuetify.lang.t("$vuetify.embeddingModel.embedding_model_creation_error"),
                  color: "error",
                },
                { root: true }
              );
            }
          )
          .finally(() => {
            this.loading = false;
          });
      } else if (step === 2) {
        // Dataset upload step
        if (stepName === "secondStep") {
          const datasetData = {
            ...this.gptSetupState,
            dataset: {
              ...this.gptSetupState.dataset,
              skipped: true,
            },
            training: {
              ...this.gptSetupState.training,
              skipped: true,
            },
          };
          this.UPDATE_GPT_SETUP_STATE(datasetData);
          this.DB_STORE_TASKS_STATUS({
            project_id: this.selectedProject.id,
            tasks_status: { ...datasetData },
          });
          this.step = step + 2;
        } else {
          const valid = !this.$refs?.dataset?.hasMetadataErrors && this.$refs?.dataset?.$refs?.dataset_form.validate();
          if (!valid) return;

          const isCloud = this.selectedGptDataset.uploadSource === "cloud";
          if (isCloud && !this.selectedGptDataset.cloudSources?.some(s => s.items?.length > 0)) {
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: this.$vuetify.lang.t("$vuetify.gpt_dataset.cloud_storage.no_file_selected"),
                color: "warning",
              },
              { root: true }
            );
            return;
          }

          if (this.datasetType === "ts") {
            const datasetTsComponent = this.$refs?.dataset?.$refs?.datasetTs;
            if (!datasetTsComponent) return;
            const valid = datasetTsComponent.$refs?.dataset_form?.validate();
            if (!valid) return;

            if (this.datasetCreateLoader) return;
            this.datasetCreateLoader = true;

            const datasetInfo = datasetTsComponent.getDatasetInfo();

            if (!datasetInfo) {
              console.warn("Dataset info could not be retrieved.");
              this.datasetCreateLoader = false;
              return;
            }
            // with time series dataset
            if (!this.datasetExist) {
              this.CREATE_GPT_DATASET_TS({
                project_id: this.$route.query.pid,
                dataset: datasetInfo,
              }).then(
                res => {
                  const datasetId = res.data.detail?.dataset_id;

                  this.UPDATE_GPT_DATASET_TS_FILE({
                    project_id: this.$route.query.pid,
                    datasetId: res.data.detail?.dataset_id,
                    ...(isCloud
                      ? {
                          cloud_sources: this.selectedGptDataset.cloudSources,
                        }
                      : { datasetFile: datasetInfo.file }),
                  })
                    .then(res => {
                      const datasetData = {
                        ...this.gptSetupState.dataset,
                        dataset_id: datasetId,
                        task_id: res.data.detail?.task_id,
                        skipped: false,
                      };
                      this.UPDATE_GPT_SETUP_STATE({
                        ...this.gptSetupState,
                        dataset: datasetData,
                        isTimeSeries: this.datasetType === "ts",
                      });
                      this.DB_STORE_TASKS_STATUS({
                        project_id: this.selectedProject.id,
                        tasks_status: {
                          ...this.gptSetupState,
                          dataset: datasetData,
                          isTimeSeries: this.datasetType === "ts",
                        },
                      }).catch(error => {
                        this.$store.dispatch(
                          `alert/${ADD_ALERT}`,
                          {
                            message: error.response,
                            color: "error",
                          },
                          { root: true }
                        );
                        console.error(error.response);
                      });

                      if (res.data.detail?.task_id) {
                        this.datasetFileUploadInProgress = true;
                        this.step = 3;
                      } else {
                        this.$store.dispatch(
                          `alert/${ADD_ALERT}`,
                          {
                            message: this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_submission_success"),
                            color: "success",
                          },
                          { root: true }
                        );
                      }
                    })
                    .catch(err => {
                      console.error("error", err);
                      if (err.response?.data?.detail?.error?.includes("Invalid encoding")) {
                        this.$store.dispatch(
                          `alert/${ADD_ALERT}`,
                          {
                            message: this.$vuetify.lang.t("$vuetify.gpt_dataset.encoding_error"),
                            color: "error",
                          },
                          { root: true }
                        );
                      } else {
                        // Cloud files are not pre-validated in the browser, so the server's text is the only explanation of what was wrong.
                        this.$store.dispatch(
                          `alert/${ADD_ALERT}`,
                          {
                            message: isCloud
                              ? this.getDatasetErrorMessage(err, true)
                              : this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_submission_error"),
                            color: "error",
                          },
                          { root: true }
                        );
                      }
                    })
                    .finally(() => {
                      this.datasetCreateLoader = false;
                    });
                },
                err => {
                  this.datasetCreateLoader = false;
                  if (this.checkDatasetTsTimezoneErrors(err)) return;
                  const errMsg = this.getDatasetErrorMessage(err);
                  Toast.error(errMsg);
                }
              );
            } else {
              let payload = this.buildDatasetUpdatePayload(this.selectedGptDataset);
              this.UPDATE_GPT_DATASET_API({
                project_id: this.$route.query.pid,
                dataset: { ...payload, datasetId: this.gptSetupState.dataset.dataset_id },
              })
                .then(
                  res => {
                    this.notifyTimezoneBackfill(res);
                    const isCloudUpdate =
                      this.selectedGptDataset.uploadSource === "cloud" &&
                      this.selectedGptDataset.cloudSourcesDirty &&
                      this.selectedGptDataset.cloudSources?.some(s => s.items?.length > 0);
                    if (this.selectedGptDataset.datasetFile || isCloudUpdate) {
                      this.UPDATE_GPT_DATASET_TS_FILE({
                        project_id: this.$route.query.pid,
                        datasetId: this.gptSetupState.dataset.dataset_id,
                        ...(isCloudUpdate
                          ? {
                              cloud_sources: this.selectedGptDataset.cloudSources,
                            }
                          : { datasetFile: this.selectedGptDataset.datasetFile }),
                      })
                        .then(res => {
                          const datasetData = {
                            ...this.gptSetupState.dataset,
                            task_id: res.data.detail?.task_id,
                            skipped: false,
                          };
                          this.UPDATE_GPT_SETUP_STATE({
                            ...this.gptSetupState,
                            dataset: datasetData,
                            isTimeSeries: this.datasetType === "ts",
                          });
                        })
                        .catch(err => {
                          const errMsg = this.getDatasetErrorMessage(err, isCloudUpdate);
                          Toast.error(errMsg);
                        });
                    } else {
                      const datasetData = {
                        ...this.gptSetupState.dataset,
                        task_id: res.data.detail?.task_id,
                      };
                      this.UPDATE_GPT_SETUP_STATE({
                        ...this.gptSetupState,
                        dataset: datasetData,
                      });
                      this.DB_STORE_TASKS_STATUS({
                        project_id: this.selectedProject.id,
                        tasks_status: {
                          ...this.gptSetupState,
                          dataset: datasetData,
                        },
                      })
                        .then(_response => {})
                        .catch(error => {
                          this.$store.dispatch(
                            `alert/${ADD_ALERT}`,
                            {
                              message: error.response,
                              color: "error",
                            },
                            { root: true }
                          );
                          console.error(error.response);
                        });
                      if (res.data.detail?.task_id) {
                        this.datasetFileUploadInProgress = true;
                      } else {
                        this.$store.dispatch(
                          `alert/${ADD_ALERT}`,
                          {
                            message: this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_update_success"),
                            color: "success",
                          },
                          { root: true }
                        );
                      }
                    }
                  },
                  _err => {
                    this.datasetCreateLoader = false;
                    if (this.checkDatasetTsTimezoneErrors(_err)) return;
                    this.$store.dispatch(
                      `alert/${ADD_ALERT}`,
                      {
                        message: this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_update_error"),
                        color: "error",
                      },
                      { root: true }
                    );
                  }
                )
                .finally(() => {
                  this.datasetCreateLoader = false;
                  const newStep = this.isSkipTraining ? step + 2 : step + 1;
                  if (this.isSkipTraining) {
                    const updatedState = {
                      ...this.gptSetupState,
                      training: { ...this.gptSetupState.training, skipped: true },
                    };
                    this.UPDATE_GPT_SETUP_STATE(updatedState);
                    this.DB_STORE_TASKS_STATUS({
                      project_id: this.selectedProject.id,
                      tasks_status: updatedState,
                    }).catch(() => {});
                  }
                  this.step = newStep;
                  this.loading = false;
                });
            }
          } else {
            this.datasetCreateLoader = true;
            if (!this.selectedGptDataset.datasetId) {
              this.loading = true;
              if (this.datasetType === "url" && this.contentSource === "webUrl") {
                this.isReachabiltyChecking = true;
              }

              this.CREATE_GPT_DATASET({
                project_id: this.$route.query.pid,
                dataset: this.selectedGptDataset,
              })
                .then(
                  res => {
                    const datasetId = res.data.detail?.dataset_id;
                    this.UPDATE_GPT_SETUP_STATE({
                      ...this.gptSetupState,
                      dataset: {
                        ...this.gptSetupState.dataset,
                        dataset_id: datasetId,
                      },
                    });
                    this.UPDATE_GPT_DATASET_STATE({
                      dataset: {
                        ...this.selectedGptDataset,
                        datasetId: datasetId,
                      },
                    });
                    const { datasetFile, is_ocr, is_text_extraction, metadata } = this.selectedGptDataset;

                    if (this.datasetType === "url" && this.contentSource === "webUrl") {
                      const urls = this.selectedGptDataset?.urls || [];
                      this.prevScrapeUrls = [...urls];
                    }

                    this.UPDATE_GPT_DATASET_FILE({
                      project_id: this.$route.query.pid,
                      datasetId: this.selectedGptDataset.datasetId,
                      ...(datasetFile && { datasetFile, is_ocr, metadata, is_text_extraction }),
                      ...(isCloud && {
                        cloud_sources: this.selectedGptDataset.cloudSources,
                        is_ocr,
                        is_text_extraction,
                        metadata,
                      }),
                      ...(this.datasetType === "url" && { scrape_urls: true }),
                    })
                      .then(res => {
                        const datasetData = {
                          ...this.gptSetupState.dataset,
                          dataset_id: datasetId,
                          task_id: res.data.detail?.task_id,
                          skipped: false,
                        };
                        this.UPDATE_GPT_SETUP_STATE({
                          ...this.gptSetupState,
                          dataset: datasetData,
                        });
                        this.DB_STORE_TASKS_STATUS({
                          project_id: this.selectedProject.id,
                          tasks_status: {
                            ...this.gptSetupState,
                            dataset: datasetData,
                          },
                        })
                          .then(_response => {})
                          .catch(error => {
                            this.$store.dispatch(
                              `alert/${ADD_ALERT}`,
                              {
                                message: error.response,
                                color: "error",
                              },
                              { root: true }
                            );
                            console.error(error.response);
                          });
                        if (res.data.detail?.task_id) {
                          this.datasetFileUploadInProgress = true;
                        } else {
                          this.$store.dispatch(
                            `alert/${ADD_ALERT}`,
                            {
                              message: this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_submission_success"),
                              color: "success",
                            },
                            { root: true }
                          );
                        }
                        const newStep = this.isSkipTraining ? step + 2 : step + 1;
                        if (this.isSkipTraining) {
                          const updatedState = {
                            ...this.gptSetupState,
                            training: { ...this.gptSetupState.training, skipped: true },
                          };
                          this.UPDATE_GPT_SETUP_STATE(updatedState);
                          this.DB_STORE_TASKS_STATUS({
                            project_id: this.selectedProject.id,
                            tasks_status: updatedState,
                          }).catch(() => {});
                        }
                        this.step = newStep;
                      })
                      .catch(err => {
                        if (err.response?.data?.detail?.error?.includes("Invalid encoding")) {
                          this.$store.dispatch(
                            `alert/${ADD_ALERT}`,
                            {
                              message: this.$vuetify.lang.t("$vuetify.gpt_dataset.encoding_error"),
                              color: "error",
                            },
                            { root: true }
                          );
                        } else {
                          // Cloud files are not pre-validated in the browser, so the server's text is the only explanation of what was wrong.
                          this.$store.dispatch(
                            `alert/${ADD_ALERT}`,
                            {
                              message: isCloud
                                ? this.getDatasetErrorMessage(err, true)
                                : this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_submission_error"),
                              color: "error",
                            },
                            { root: true }
                          );
                        }
                      })
                      .finally(() => {
                        this.datasetCreateLoader = false;
                      });
                  },
                  err => {
                    this.datasetCreateLoader = false;
                    if (this.datasetType === "url" && this.contentSource === "webUrl") {
                      this.checkUrlReachability(err);
                      this.checkUrlDuplicacy(err, this.selectedGptDataset?.urls ?? []);
                    } else {
                      const errMsg = this.getDatasetErrorMessage(err);
                      Toast.error(errMsg);
                    }
                  }
                )
                .finally(() => {
                  this.loading = false;
                  if (this.datasetType === "url" && this.contentSource === "webUrl") {
                    this.isReachabiltyChecking = false;
                  }
                });
            } else {
              this.loading = true;
              setTimeout(() => {
                let payload = this.buildDatasetUpdatePayload(this.selectedGptDataset);

                if (this.datasetType === "url" && this.contentSource === "webUrl") {
                  this.isReachabiltyChecking = true;
                }

                this.UPDATE_GPT_DATASET_API({
                  project_id: this.$route.query.pid,
                  dataset: {
                    ...payload,
                    datasetId: this.selectedGptDataset.datasetId,
                    ...(!["url", "ts"].includes(this.datasetType) && this.selectedGptDataset?.datasetFile
                      ? { datasetFile: this.selectedGptDataset.datasetFile }
                      : {}),
                    ...(this.selectedGptDataset.uploadSource === "cloud" &&
                      this.selectedGptDataset.cloudSourcesDirty &&
                      !["url", "ts"].includes(this.datasetType)
                      ? { cloudSources: this.selectedGptDataset.cloudSources }
                      : {}),
                  },
                })
                  .then(res => {
                    const datasetData = {
                      ...this.gptSetupState.dataset,
                      task_id: res.data.detail?.task_id,
                    };

                    this.UPDATE_GPT_SETUP_STATE({
                      ...this.gptSetupState,
                      dataset: datasetData,
                    });

                    // acknowledge this api that urls is changed and start scrapping
                    if (this.datasetType === "url" && this.isUrlsChanged(this.prevScrapeUrls, payload?.urls)) {
                      if (this.contentSource === "webUrl") {
                        const urls = payload?.urls || [];
                        this.prevScrapeUrls = [...urls];
                      }

                      this.UPDATE_GPT_DATASET_FILE({
                        project_id: this.$route.query.pid,
                        datasetId: this.selectedGptDataset.datasetId,
                        ...(this.datasetType === "url" && { scrape_urls: true }),
                      }).catch(() => {
                        const errMsg = this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_update_error");
                        Toast.error(errMsg);
                      });
                    }

                    this.DB_STORE_TASKS_STATUS({
                      project_id: this.selectedProject.id,
                      tasks_status: {
                        ...this.gptSetupState,
                        dataset: datasetData,
                      },
                    })
                      .then(_response => {})
                      .catch(error => {
                        this.$store.dispatch(
                          `alert/${ADD_ALERT}`,
                          {
                            message: error.response,
                            color: "error",
                          },
                          { root: true }
                        );
                        console.error(error.response);
                      });

                    if (res.data.detail?.task_id) {
                      this.datasetFileUploadInProgress = true;
                    } else {
                      this.UPDATE_GPT_SETUP_STATE({
                        ...this.gptSetupState,
                        dataset: {
                          ...this.gptSetupState.dataset,
                          completed: true,
                          task_id: res.data.detail?.task_id,
                        },
                      });
                      const msg = this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_update_success");
                      Toast.success(msg);
                    }

                    const newStep = this.isSkipTraining ? step + 2 : step + 1;
                    if (this.isSkipTraining) {
                      const updatedState = {
                        ...this.gptSetupState,
                        training: { ...this.gptSetupState.training, skipped: true },
                      };
                      this.UPDATE_GPT_SETUP_STATE(updatedState);
                      this.DB_STORE_TASKS_STATUS({
                        project_id: this.selectedProject.id,
                        tasks_status: updatedState,
                      }).catch(() => {});
                    }
                    this.step = newStep;
                  })
                  .catch(err => {
                    if (this.datasetType === "url" && this.contentSource === "webUrl") {
                      this.checkUrlReachability(err);
                      this.checkUrlDuplicacy(err, this.selectedGptDataset?.urls ?? []);
                    } else {
                      const errMsg = this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_update_error");
                      Toast.error(errMsg);
                    }
                  })
                  .finally(() => {
                    this.datasetCreateLoader = false;
                    this.loading = false;
                    if (this.datasetType === "url" && this.contentSource === "webUrl") {
                      this.isReachabiltyChecking = false;
                    }
                  });
              }, 100);
            }
          }
        }
      } else if (step === 3) {
        if (stepName === "thirdStep") {
          const trainData = {
            ...this.gptSetupState,
            training: {
              ...this.gptSetupState.training,
              skipped: true,
            },
          };
          this.UPDATE_GPT_SETUP_STATE(trainData);
          this.DB_STORE_TASKS_STATUS({
            project_id: this.selectedProject.id,
            tasks_status: trainData,
          })
            .then(_response => {})
            .catch(error => {
              console.error(error.response);
            });
        }
        this.step = step + 1;
      } else if (step === 4) {
        if (stepName === "forthstep") {
          const profileData = {
            ...this.gptSetupState,
            // finalSkipped: true,
            completed: true,
            gpt_profile: {
              ...this.gptSetupState.gpt_profile,
              skipped: true,
            },
          };
          this.UPDATE_GPT_SETUP_STATE(profileData);
          this.DB_STORE_TASKS_STATUS({
            project_id: this.selectedProject.id,
            tasks_status: profileData,
          })
            .then(_response => {})
            .catch(error => {
              console.error(error.response);
            });

          this.$router.push({
            path: `/projects`,
            query: {
              pid: this.$route.query.pid,
              stype: this.$route.query.stype,
              ptab: "mairaDatasets",
            },
          });
          return; // Exit early so normal step-4 code won't run
        }
        const valid = this.$refs.profile.validateForm();
        if (!valid) {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t("$vuetify.gpt_profile.profile_validation_error"),
              color: "red",
            },
            { root: true }
          );
          return;
        }

        if (!this.profileExist) {
          this.loading = true;
          setTimeout(() => {
            this.CREATE_GPT_PROFILE({
              project_id: this.$route.query.pid,
              profile: this.selectedGptProfile,
            })
              .then(
                res => {
                  // Mark setup as complete
                  const profileData = {
                    ...this.gptSetupState,
                    // finalSkipped: true,
                    completed: true,
                    gpt_profile: {
                      completed: true,
                      profile_id: res.data.detail?.profile_id,
                    },
                  };
                  this.UPDATE_GPT_SETUP_STATE(profileData);
                  this.DB_STORE_TASKS_STATUS({
                    project_id: this.selectedProject.id,
                    tasks_status: profileData,
                  })
                    .then(_response => {})
                    .catch(error => {
                      console.error(error.response);
                    });
                  this.$store.dispatch(
                    `alert/${ADD_ALERT}`,
                    {
                      message: this.$vuetify.lang.t("$vuetify.gpt_profile.profile_creation_success"),
                      color: "success",
                    },
                    { root: true }
                  );
                  // Set default profile
                  this.CREATE_GPT_SETTINGS({
                    project_id: this.$route.query.pid,
                    default_profile: res.data?.detail?.profile_id,
                  })
                    .then(_res => {})
                    .catch(err => {
                      console.error("default profile creation error", err);
                    });
                  this.$router.push({
                    path: `/projects`,
                    query: {
                      pid: this.$route.query.pid,
                      stype: this.$route.query.stype,
                      ptab: "mairaDatasets",
                    },
                  });
                },
                err => {
                  console.error("Err ", err);
                  this.$store.dispatch(
                    `alert/${ADD_ALERT}`,
                    {
                      message:
                        err.response?.data?.detail?.response ||
                        this.$vuetify.lang.t("$vuetify.gpt_profile.profile_creation_error"),
                      color: "error",
                    },
                    { root: true }
                  );
                }
              )
              .finally(() => {
                this.loading = false;
              });
          }, 100);
        } else {
          this.loading = true;
          setTimeout(() => {
            this.UPDATE_GPT_PROFILE_API({
              project_id: this.$route.query.pid,
              profile: this.selectedGptProfile,
            })
              .then(
                _res => {
                  const profileData = {
                    ...this.gptSetupState,
                    completed: true,
                    gpt_profile: {
                      ...this.gptSetupState.gpt_profile,
                      completed: true,
                    },
                  };
                  this.UPDATE_GPT_SETUP_STATE(profileData);

                  this.$store.dispatch(
                    `alert/${ADD_ALERT}`,
                    {
                      message: this.$vuetify.lang.t("$vuetify.gpt_profile.profile_update_success"),
                      color: "success",
                    },
                    { root: true }
                  );
                },
                _err => {
                  this.$store.dispatch(
                    `alert/${ADD_ALERT}`,
                    {
                      message: this.$vuetify.lang.t("$vuetify.gpt_profile.profile_update_error"),
                      color: "error",
                    },
                    { root: true }
                  );
                }
              )
              .finally(() => {
                this.loading = false;
              });
          }, 100);
        }
      }
    },
    handleTraining() {
      const valid = this.validateForm();
      if (!valid) return;
      if (this.isSkipTraining) return;
      this.loading = true;
      this.TRAIN_GPT_DATASET({
        project_id: this.$route.query.pid,
        datasetId: this.selectedGptDataset.datasetId,
        trainType: this.trainType,
        batchSize: this.batchSize,
      })
        .then(
          res => {
            const trainingData = {
              ...this.gptSetupState.training,
              task_id: res.data?.detail?.task_id,
            };
            this.UPDATE_GPT_SETUP_STATE({
              ...this.gptSetupState,
              training: trainingData,
            });
            this.DB_STORE_TASKS_STATUS({
              project_id: this.selectedProject.id,
              tasks_status: {
                ...this.gptSetupState,
                training: trainingData,
              },
            })
              .then(_response => {})
              .catch(error => {
                console.error(error.response);
              });
            this.trainingInProgress = true;
          },
          err => {
            console.error("train err", err.response);
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: err.response?.data?.detail?.response || "Dataset training error",
                color: "error",
              },
              { root: true }
            );
          }
        )
        .finally(() => {
          this.loading = false;
        });
    },
    validateForm() {
      return this.$refs.train_form.validate();
    },
    onModelLoaded(model) {
      this.UPDATE_GPT_SETUP_STATE({
        ...this.gptSetupState,
        embedding: {
          completed: true,
          model: model,
        },
      });
    },
  },
};
</script>

<style>
.v-stepper__header {
  box-shadow: none !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
